import React, { forwardRef, useMemo } from "react";
import { TextField, Paper } from "@mui/material";
import { alpha, styled } from "@mui/material/styles";

const MIN_ALLOWED_DATE = "1920-01-01";
const BLOCKED_NUMBER_KEYS = new Set(["e", "E", "+", "-"]);

const StyledTextField = styled(TextField)(({ theme }) => ({
  "& .MuiOutlinedInput-root": {
    borderRadius: 18,
    backgroundColor: alpha(theme.palette.common.white, 0.9),
    boxShadow: "0 12px 35px rgba(15,23,42,0.08)",
    transition: "all 0.25s ease",
    "& fieldset": {
      borderColor: alpha(theme.palette.primary.main, 0.2),
    },
    "&:hover fieldset": {
      borderColor: theme.palette.primary.main,
    },
    "&.Mui-focused": {
      boxShadow: `0 15px 40px ${alpha(theme.palette.primary.main, 0.25)}`,
    },
    "&.Mui-focused fieldset": {
      borderWidth: 1.5,
      borderColor: theme.palette.primary.main,
    },
  },
  "& .MuiInputAdornment-root": {
    color: theme.palette.primary.main,
  },
}));

export const ModernTextField = forwardRef(
  (
    {
      InputLabelProps,
      SelectProps,
      inputProps,
      onKeyDown,
      select,
      type,
      ...other
    },
    ref
  ) => {
    const mergedInputLabelProps = {
      ...(InputLabelProps || {}),
    };

    if (select && mergedInputLabelProps.shrink === undefined) {
      mergedInputLabelProps.shrink = true;
    }

    const mergedSelectProps = {
      ...(SelectProps || {}),
    };

    if (select && mergedSelectProps.displayEmpty === undefined) {
      mergedSelectProps.displayEmpty = true;
    }

    const mergedInputProps = {
      ...(inputProps || {}),
    };

    const isNumberInput = type === "number";
    const isDateInput = type === "date";

    if (isNumberInput) {
      if (mergedInputProps.min === undefined) {
        mergedInputProps.min = 0;
      }
      if (!mergedInputProps.inputMode) {
        mergedInputProps.inputMode = "decimal";
      }
      if (!mergedInputProps.pattern) {
        mergedInputProps.pattern = "[0-9]*([.,][0-9]+)?";
      }
    }

    if (isDateInput) {
      const providedMin = mergedInputProps.min;
      if (!providedMin || providedMin < MIN_ALLOWED_DATE) {
        mergedInputProps.min = MIN_ALLOWED_DATE;
      }
    }

    const handleKeyDown = useMemo(() => {
      if (!isNumberInput && !onKeyDown) {
        return undefined;
      }

      return (event) => {
        if (isNumberInput && BLOCKED_NUMBER_KEYS.has(event.key)) {
          event.preventDefault();
          return;
        }

        if (onKeyDown) {
          onKeyDown(event);
        }
      };
    }, [isNumberInput, onKeyDown]);

    return (
      <StyledTextField
        {...other}
        type={type}
        select={select}
        ref={ref}
        inputProps={
          Object.keys(mergedInputProps).length ? mergedInputProps : undefined
        }
        onKeyDown={handleKeyDown}
        InputLabelProps={
          Object.keys(mergedInputLabelProps).length
            ? mergedInputLabelProps
            : undefined
        }
        SelectProps={
          Object.keys(mergedSelectProps).length ? mergedSelectProps : undefined
        }
      />
    );
  }
);

export const GlassPanel = styled(Paper)(({ theme }) => ({
  borderRadius: 32,
  padding: theme.spacing(3),
  backgroundColor: alpha(theme.palette.background.paper, 0.98),
  border: `1px solid ${alpha(theme.palette.primary.main, 0.15)}`,
  boxShadow: "0 25px 80px rgba(15,23,42,0.12)",
  backdropFilter: "blur(12px)",
}));
